<script lang="ts">
  // Fees editor per INTERFACE-SPECIFICATION.md §4.4 (Fee Schedule —
  // Matrix View) and §6 (Detail / Edit Mode).
  //
  // The maintenance interface previously did not surface the fees
  // schedule in the Catalogue Detail view. PR 4.1 adds an inline
  // editor gated on proposer:fees covering the four mutable fee
  // surfaces:
  //
  // - establishment_fee.tiers — add / remove / edit per-tier
  //   min_amount, max_amount, fee. Tier-contiguity is a server-side
  //   invariant; per Greg's D3 sign-off on the build plan, this
  //   editor surfaces tier-coverage issues at submit time via the
  //   existing pre-submission validation panel rather than live.
  // - dishonour_fee — single non-negative number.
  // - overdue_fee — single non-negative number.
  // - included_in_comparison_rate — checkbox set over the known
  //   fee-type identifiers.
  //
  // The schedule_id is not surfaced for direct editing — it is an
  // opaque identifier tracked at the schedule layer and not intended
  // to be hand-mutated through the maintenance interface.
  //
  // Per-credit-rating establishment-fee overrides
  // (`establishment_fee.grade_overrides[]` in the upstream
  // source-of-truth product catalogue) are out of scope for PR 4.1
  // pending the SPEC-CHANGE-CANDIDATES item 13 spec amendment.

  import {
    stageFeeEdit,
    unstageFeeEdit,
    stagedEditsState
  } from '../lib/staged-edits.svelte';
  import { navigate } from '../lib/router.svelte';
  import type { ProductCatalogueFile, ProposeResponse } from '../lib/types';
  import type { EstablishmentFeeTier, FeesShape } from '../lib/staged-edits';
  import { formatAmount } from '../lib/formatters';
  import SubmitForReview from './SubmitForReview.svelte';
  import ValueTypeLockBanner from './ValueTypeLockBanner.svelte';

  interface Props {
    catalogue: ProductCatalogueFile;
  }

  const { catalogue }: Props = $props();

  const baseFees = $derived((catalogue.fees ?? {}) as FeesShape);

  // The staged-fees view is the merged result of base + staged-fee
  // edits, so the editor inputs always reflect what would be sent on
  // submit. Each top-level fees path is replaced wholesale when
  // staged; unstaged paths fall back to the catalogue baseline.
  const stagedFees = $derived.by<FeesShape>(() => {
    const merged: FeesShape = { ...baseFees };
    for (const path of ['fees.establishment_fee', 'fees.dishonour_fee', 'fees.overdue_fee', 'fees.included_in_comparison_rate'] as const) {
      const edit = stagedEditsState.fee_edits[path];
      if (!edit) continue;
      if (path === 'fees.establishment_fee') merged.establishment_fee = edit.staged as { tiers: EstablishmentFeeTier[] };
      else if (path === 'fees.dishonour_fee') merged.dishonour_fee = edit.staged as number;
      else if (path === 'fees.overdue_fee') merged.overdue_fee = edit.staged as number;
      else if (path === 'fees.included_in_comparison_rate') merged.included_in_comparison_rate = edit.staged as string[];
    }
    return merged;
  });

  // Lock when the store holds edits of a different value type.
  // Reactive on store state so a discard / submit elsewhere clears
  // the lock automatically.
  const locked = $derived(
    stagedEditsState.valueType !== null && stagedEditsState.valueType !== 'fees'
  );

  let submitOpen = $state<boolean>(false);

  function openSubmit() { submitOpen = true; }
  function closeSubmit() { submitOpen = false; }
  function onProposalCreated(response: ProposeResponse) {
    submitOpen = false;
    navigate(`/changes/pending-review/${response.pr_number}`);
  }

  function trySetFee(path: 'fees.dishonour_fee' | 'fees.overdue_fee', raw: string) {
    if (!/^\d+(\.\d+)?$/.test(raw.trim())) return;
    const value = Number(raw.trim());
    if (!Number.isFinite(value) || value < 0) return;
    const before = path === 'fees.dishonour_fee' ? baseFees.dishonour_fee : baseFees.overdue_fee;
    stageFeeEdit(path, before, value);
  }

  function toggleComparisonRateInclusion(feeType: string, included: boolean) {
    const baseList = baseFees.included_in_comparison_rate ?? [];
    const stagedList = stagedFees.included_in_comparison_rate ?? [];
    const nextList = included
      ? Array.from(new Set([...stagedList, feeType])).sort()
      : stagedList.filter((entry) => entry !== feeType);
    stageFeeEdit('fees.included_in_comparison_rate', baseList, nextList);
  }

  function updateTiers(nextTiers: EstablishmentFeeTier[]) {
    const before = baseFees.establishment_fee ?? { tiers: [] };
    const staged = { ...before, tiers: nextTiers };
    stageFeeEdit('fees.establishment_fee', before, staged);
  }

  function setTierField(index: number, field: keyof EstablishmentFeeTier, raw: string) {
    if (!/^\d+(\.\d+)?$/.test(raw.trim())) return;
    const value = Number(raw.trim());
    const tiers = (stagedFees.establishment_fee?.tiers ?? []).map((tier, i) =>
      i === index ? { ...tier, [field]: value } : { ...tier }
    );
    updateTiers(tiers);
  }

  function removeTier(index: number) {
    const tiers = (stagedFees.establishment_fee?.tiers ?? []).filter((_, i) => i !== index);
    updateTiers(tiers);
  }

  function addTier() {
    const existing = stagedFees.establishment_fee?.tiers ?? [];
    const lastMax = existing.length > 0 ? existing[existing.length - 1]!.max_amount : 0;
    const nextTier: EstablishmentFeeTier = {
      min_amount: lastMax + 1,
      max_amount: lastMax + 1,
      fee: 0
    };
    updateTiers([...existing, nextTier]);
  }

  function revertEstablishmentFee() {
    unstageFeeEdit('fees.establishment_fee');
  }
  function revertDishonour() { unstageFeeEdit('fees.dishonour_fee'); }
  function revertOverdue() { unstageFeeEdit('fees.overdue_fee'); }
  function revertComparisonInclusion() { unstageFeeEdit('fees.included_in_comparison_rate'); }

  const COMPARISON_RATE_FEE_TYPES = ['establishment_fee', 'dishonour_fee', 'overdue_fee'] as const;
  const stagedCount = $derived(Object.keys(stagedEditsState.fee_edits).length);

  const isEstFeeStaged = $derived(stagedEditsState.fee_edits['fees.establishment_fee'] !== undefined);
  const isDishonourStaged = $derived(stagedEditsState.fee_edits['fees.dishonour_fee'] !== undefined);
  const isOverdueStaged = $derived(stagedEditsState.fee_edits['fees.overdue_fee'] !== undefined);
  const isInclusionStaged = $derived(stagedEditsState.fee_edits['fees.included_in_comparison_rate'] !== undefined);
</script>

<section class="fees-editor" class:is-locked={locked}>
  {#if locked && stagedEditsState.valueType}
    <ValueTypeLockBanner
      currentValueType={stagedEditsState.valueType}
      attemptedValueType="fees"
    />
  {/if}

  <p class="muted">Tier coverage and contiguity are enforced server-side. The pre-submission validation panel surfaces gap or overlap warnings before the proposal opens.</p>

  <aside class="banner banner-info override-note">
    <strong>Per-credit-rating overrides take precedence.</strong>
    A populated <code>establishment_fee_override</code> on a credit-rating-axis-level node (for example,
    <code>rate_cube.950_plus.establishment_fee_override</code> on AU unsec) replaces the product-level fee
    for every cell under that credit-rating. Edit overrides on the Rate Cube tab — each credit-rating
    row carries an inline <strong>est.fee</strong> sub-line. Overrides and product-level tiers share the
    <strong>fees</strong> value-type bucket (<code>GOVERNANCE-ARCHITECTURE.md</code> §9.1), so the same
    reviewer authority approves both.
  </aside>

  <h4 class="section-heading">Establishment fee tiers</h4>
  <table class="fees-table">
    <thead>
      <tr>
        <th class="num">Min amount</th>
        <th class="num">Max amount</th>
        <th class="num">Fee</th>
        <th class="actions">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each stagedFees.establishment_fee?.tiers ?? [] as tier, index (index)}
        <tr>
          <td class="num">
            <input
              type="text"
              inputmode="numeric"
              aria-label="Tier {index + 1} minimum amount"
              value={tier.min_amount}
              disabled={locked}
              oninput={(e) => setTierField(index, 'min_amount', (e.target as HTMLInputElement).value)}
            />
          </td>
          <td class="num">
            <input
              type="text"
              inputmode="numeric"
              aria-label="Tier {index + 1} maximum amount"
              value={tier.max_amount}
              disabled={locked}
              oninput={(e) => setTierField(index, 'max_amount', (e.target as HTMLInputElement).value)}
            />
          </td>
          <td class="num">
            <input
              type="text"
              inputmode="decimal"
              aria-label="Tier {index + 1} fee"
              value={tier.fee}
              disabled={locked}
              oninput={(e) => setTierField(index, 'fee', (e.target as HTMLInputElement).value)}
            />
          </td>
          <td class="actions">
            <button class="btn-link" type="button" disabled={locked} onclick={() => removeTier(index)}>Remove</button>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
  <div class="fees-tier-actions">
    <button class="btn-link" type="button" disabled={locked} onclick={addTier}>+ Add tier</button>
    {#if isEstFeeStaged}
      <button class="btn-link" type="button" disabled={locked} onclick={revertEstablishmentFee}>Revert tier changes</button>
    {/if}
  </div>

  <h4 class="section-heading">Other fees</h4>
  <table class="fees-table">
    <thead>
      <tr>
        <th>Field</th>
        <th class="num">Current</th>
        <th class="num">Staged</th>
        <th class="actions">Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr class:is-staged={isDishonourStaged}>
        <td><strong>Dishonour fee</strong></td>
        <td class="num">${formatAmount(baseFees.dishonour_fee ?? 0)}</td>
        <td class="num">
          <input
            type="text"
            inputmode="decimal"
            aria-label="Dishonour fee"
            value={stagedFees.dishonour_fee ?? ''}
            disabled={locked}
            oninput={(e) => trySetFee('fees.dishonour_fee', (e.target as HTMLInputElement).value)}
          />
        </td>
        <td class="actions">
          {#if isDishonourStaged}
            <button class="btn-link" type="button" disabled={locked} onclick={revertDishonour}>Revert</button>
          {/if}
        </td>
      </tr>
      <tr class:is-staged={isOverdueStaged}>
        <td><strong>Overdue fee</strong></td>
        <td class="num">${formatAmount(baseFees.overdue_fee ?? 0)}</td>
        <td class="num">
          <input
            type="text"
            inputmode="decimal"
            aria-label="Overdue fee"
            value={stagedFees.overdue_fee ?? ''}
            disabled={locked}
            oninput={(e) => trySetFee('fees.overdue_fee', (e.target as HTMLInputElement).value)}
          />
        </td>
        <td class="actions">
          {#if isOverdueStaged}
            <button class="btn-link" type="button" disabled={locked} onclick={revertOverdue}>Revert</button>
          {/if}
        </td>
      </tr>
    </tbody>
  </table>

  <h4 class="section-heading">Included in comparison rate</h4>
  <p class="muted">The comparison rate formula (NCCP Reg 100 for AU) includes the fees ticked below. Changes here affect how the customer-facing comparison rate is calculated.</p>
  <div class="comparison-inclusion">
    {#each COMPARISON_RATE_FEE_TYPES as feeType (feeType)}
      {@const included = (stagedFees.included_in_comparison_rate ?? []).includes(feeType)}
      <label class="comparison-inclusion__option">
        <input
          type="checkbox"
          checked={included}
          disabled={locked}
          onchange={(e) => toggleComparisonRateInclusion(feeType, (e.target as HTMLInputElement).checked)}
        />
        <span>{feeType.replaceAll('_', ' ')}</span>
      </label>
    {/each}
    {#if isInclusionStaged}
      <button class="btn-link" type="button" disabled={locked} onclick={revertComparisonInclusion}>Revert inclusion changes</button>
    {/if}
  </div>

  {#if !locked && stagedCount > 0}
    <div class="fees-editor__actions">
      <span class="muted">{stagedCount} fee path{stagedCount === 1 ? '' : 's'} staged</span>
      <button class="btn btn-primary" type="button" onclick={openSubmit}>Submit for review →</button>
    </div>
  {/if}
</section>

{#if submitOpen}
  <SubmitForReview
    {catalogue}
    valueType="fees"
    onClose={closeSubmit}
    onSubmitted={onProposalCreated}
  />
{/if}

<style>
  .fees-editor {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .override-note {
    font-size: var(--type-sm);
    line-height: 1.5;
  }
  .override-note code {
    font-family: var(--font-mono);
    font-size: 0.92em;
  }
  /* Mute the editor body when locked so the operator's eye is drawn
     to the ValueTypeLockBanner's Discard affordance. The banner
     itself sits above and is rendered at full opacity via the
     ValueTypeLockBanner component's own styles. */
  .fees-editor.is-locked > :not(:first-child) {
    opacity: 0.55;
  }
  .fees-table {
    width: 100%;
    border-collapse: collapse;
  }
  .fees-table th,
  .fees-table td {
    padding: var(--space-2) var(--space-3);
    border-bottom: 1px solid var(--surface-line-soft);
    text-align: left;
    vertical-align: middle;
  }
  .fees-table th.num,
  .fees-table td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-family: var(--font-mono);
  }
  .fees-table th.actions,
  .fees-table td.actions {
    text-align: right;
  }
  .fees-table input {
    width: 8rem;
    padding: var(--space-1) var(--space-2);
    text-align: right;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }
  .fees-table tr.is-staged {
    background: var(--surface-highlight, transparent);
  }
  .fees-tier-actions {
    display: flex;
    gap: var(--space-3);
    padding-top: var(--space-1);
  }
  .comparison-inclusion {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    align-items: center;
  }
  .comparison-inclusion__option {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-family: var(--font-mono);
    font-size: var(--type-sm);
  }
  .fees-editor__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: var(--space-2);
  }
</style>
